import React, { useEffect, useState } from "react";
import {
  DialogActions,
  Button,
  IconButton,
  Grid,
  Paper,
  Select,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import { omsService } from "../../utils/services/omsProvider";
import { pipe17Service, needsOrgKey } from "../../utils/services/pipe17Service";
import {
  acendaService,
  acendaCredentialsComplete,
  buildOnboardPayload,
  ACENDA_PROVIDER_NAME,
  AUTH_MODES,
} from "../../utils/services/acendaService";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";

// Pipe17 has no "shop name" / extra-info concept in this generic form — its
// onboarding needs just the client's API key; org_key, webhook registration,
// and scope validation are handled entirely server-side by manage-pipe17 (see
// pipe17-onboard's flow, which this modal now also drives for consistency).
const PIPE17_PROVIDER_NAME = "Pipe17";

// Acenda, like Pipe17, bypasses the generic oms-config form: manage-acenda
// validates the credentials against every endpoint the integration needs and
// derives the ship-advice routing from the org's warehouses before writing
// anything. Unlike Pipe17 it needs the ORGANIZATION typed in — that is the
// `X-Astur-Organization` header, not a record Acenda exposes, so there is
// nothing to auto-detect — and it accepts either of the two credential shapes
// Acenda issues (see acendaService).

const OMSManagementForm = ({
  selectedClient,
  handleClose,
  selectedData,
  onAddProviderData,
  onDeleteSuccess,
  setLoading,
  providerList = [],
  viewOnly,
}) => {
  const InitialData = [
    {
      provider: "",
      shop_name: "",
      api_key: "",
      org_key: "",
      orgKeyNeeded: false,
      organization: "",
      auth_mode: AUTH_MODES.OAUTH,
      acenda_client_id: "",
      acenda_client_secret: "",
      extra_info: [{ key: "", value: "" }],
    },
  ];
  const [providers, setProviders] = useState(InitialData);
  const getProviderName = (providerId) =>
    providerList.find((p) => p._id === providerId)?.name;
  const isPipe17 = (provider) =>
    getProviderName(provider.provider) === PIPE17_PROVIDER_NAME;
  const isAcenda = (provider) =>
    getProviderName(provider.provider) === ACENDA_PROVIDER_NAME;
  // Both take their credentials inline here instead of a shop name.
  const isDirectOnboard = (provider) => isPipe17(provider) || isAcenda(provider);
  const handleAddProvider = () => {
    setProviders((prevProviders) => [
      ...prevProviders,
      {
        provider: {},
        shop_name: "",
        api_key: "",
        org_key: "",
        orgKeyNeeded: false,
        organization: "",
        auth_mode: AUTH_MODES.OAUTH,
        acenda_client_id: "",
        acenda_client_secret: "",
        extra_info: [{ key: "", value: "" }],
      },
    ]);
  };

  const handleRemoveProvider = async (index, provider) => {
    setLoading(true);
    if (selectedData?.length) {
      try {
        const omsResponse = await omsService.delete(provider?._id);
        if (omsResponse) {
          onDeleteSuccess && onDeleteSuccess();
          setProviders((prevProviders) =>
            prevProviders.filter((_, i) => i !== index),
          );
          setLoading(false);
          handleClose && handleClose();
        }
      } catch (error) {
        console.error("Delete failed:", error);
        handleClose && handleClose();
      }
      setLoading(false);
    } else {
      setProviders((prevProviders) =>
        prevProviders.filter((_, i) => i !== index),
      );
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    const newProviders = [...providers];
    newProviders[index][field] = value;
    setProviders(newProviders);
  };

  useEffect(() => {
    setProviders(
      selectedData.map((x) =>
        x.extra_info?.length
          ? x
          : { ...x, extra_info: [{ key: "", value: "" }] },
      ),
    );
  }, [selectedData]);

  async function addConfig(configurations) {
    for (const [index, config] of configurations.entries()) {
      if (isPipe17(config)) {
        // Pipe17 doesn't go through the generic oms-config create/edit path —
        // it needs the real onboarding flow (scope validation, org_key
        // auto-resolution, credential save, webhook registration), all done
        // server-side by manage-pipe17. This call is upsert-friendly, so it
        // also covers re-entering the key for an already-onboarded client.
        try {
          const payload = {
            client_id: config.client_id,
            api_key: config.api_key,
          };
          // org_key is normally auto-resolved from the API key on the backend;
          // only send it once a prior attempt has told us (via needsOrgKey)
          // that the account has no orders/products/webhooks yet to resolve
          // it from.
          if (config.orgKeyNeeded && config.org_key?.trim()) {
            payload.org_key = config.org_key.trim();
          }
          const res = await pipe17Service.onboard(payload);
          if (res?.success === false) {
            if (needsOrgKey(res.message)) {
              setProviders((prev) =>
                prev.map((p, i) =>
                  i === index ? { ...p, orgKeyNeeded: true } : p,
                ),
              );
            }
            toast.error(
              res.message || "Pipe17 onboarding did not complete — see details",
            );
            return;
          }
          toast.success("Pipe17 client onboarded and webhook registered");
        } catch (error) {
          // Thrown (non-2xx) errors are already toasted by axiosInstance's
          // response interceptor — don't double up, just react to it here:
          // if it's the "couldn't read org_key off any orders/products/
          // webhooks" case (typically a brand-new account), surface the org
          // key field just for this row so the admin can retry with it set,
          // instead of failing with no way to recover.
          const message = error.response?.data?.message;
          if (needsOrgKey(message)) {
            setProviders((prev) =>
              prev.map((p, i) =>
                i === index ? { ...p, orgKeyNeeded: true } : p,
              ),
            );
          }
          console.error("Pipe17 onboarding failed:", error);
          return;
        }
        handleClose && handleClose();
      } else if (isAcenda(config)) {
        // Same reasoning as Pipe17 above: manage-acenda owns credential
        // validation, warehouse routing and the provider/oms-config docs, and
        // the call is an upsert so re-entering credentials for an already
        // onboarded client goes through this same path.
        try {
          const res = await acendaService.onboard(buildOnboardPayload(config));
          if (res?.success === false) {
            toast.error(
              res.message || "Acenda onboarding did not complete — see details",
            );
            return;
          }
          const scoped = res?.routing?.warehouses
            ? ` — ship advices scoped to warehouses ${res.routing.warehouses.join(",")}`
            : res?.routing?.warehouse_id
              ? ` — ship advices scoped to warehouse ${res.routing.warehouse_id}`
              : "";
          toast.success(`Acenda client onboarded${scoped}`);
        } catch (error) {
          // Non-2xx is already toasted by axiosInstance's response interceptor.
          console.error("Acenda onboarding failed:", error);
          return;
        }
        handleClose && handleClose();
      } else if (selectedData?.length) {
        try {
          const response = await omsService.edit(
            {
              provider: config?.provider,
              shop_name: config?.shop_name,
              extra_info: config?.extra_info,
            },
            config?._id,
          );
          console.log(
            `Successfully added configuration for ${config.provider}`,
          );
          console.log(response.data);
          handleClose && handleClose();
        } catch (error) {
          console.error(
            `Error adding configuration for ${config.provider}:`,
            error.message,
          );
        }
      } else {
        if (config?.provider && config?.shop_name) {
          try {
            const response = await omsService.create(config);
            console.log(
              `Successfully added configuration for ${config.provider}`,
            );
            console.log(response.data);
            handleClose && handleClose();
          } catch (error) {
            console.error(
              `Error adding configuration for ${config.provider}:`,
              error.message,
            );
            return;
          }
        } else {
          toast.error("Please provide all required Information!", {
            autoClose: 3000,
          });
          return;
        }
      }
      setProviders(InitialData);
      onAddProviderData && onAddProviderData();
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    addConfig(
      providers.map((item) => ({ ...item, client_id: selectedClient?._id })),
    );
  };

  const manageExtraInfo = (index, authIndex, field, value) => {
    const newProviders = [...providers];
    newProviders[index].extra_info = newProviders[index].extra_info.map(
      (x, i) => (i == authIndex ? { ...x, [field]: value } : x),
    );
    setProviders(newProviders);
  };

  const addNewAuthDetails = (e, index) => {
    e.preventDefault();
    const newProviders = [...providers];
    newProviders[index].extra_info = [
      ...(newProviders[index].extra_info || []),
      { key: "", value: "" },
    ];
    setProviders(newProviders);
  };

  const removeAuthDetail = (providerIndex, detailIndex) => {
    setProviders((prevState) => {
      const newState = [...prevState];
      if (newState[providerIndex]?.extra_info) {
        newState[providerIndex].extra_info = newState[
          providerIndex
        ].extra_info.filter((_, i) => i !== detailIndex);
      }
      return newState;
    });
  };
  const isFormValid = providers.every((provider) => {
    if (isPipe17(provider))
      return (
        provider.provider &&
        provider.api_key &&
        (!provider.orgKeyNeeded || provider.org_key?.trim())
      );
    if (isAcenda(provider))
      return Boolean(provider.provider) && acendaCredentialsComplete(provider);
    return provider.provider && provider.shop_name;
  });
  return (
    <form onSubmit={handleSubmit}>
      {providers.map((provider, index) => (
        <Paper
          key={index}
          variant="outlined"
          style={{
            backgroundColor: "var(--bg-paper)",
            color: "var(--text-primary)",
            padding: "16px",
            marginTop: "16px",
            background: "var(--hover-bg)",
            borderRadius: "4px",
          }}
        >
          {
            <Grid>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  style={{
                    // backgroundColor: "var(--bg-paper)",
                    color: "var(--text-primary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    fontWeight={500}
                  >{`Provider${index + 1}`}</Typography>
                  {selectedData?.length ? (
                    <></>
                  ) : (
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleRemoveProvider(index, provider)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: 13,
                        color: "var(--text-primary)",
                      }}
                    >
                      Select Provider <span style={{ color: "red" }}>*</span>
                    </label>

                    <Select
                      disabled={viewOnly}
                      style={{
                        backgroundColor: "var(--bg-paper)",
                        color: "var(--text-primary)",
                        marginTop: 5,
                        fontSize: 13,
                        backgroundColor: "var(--bg-paper)",
                        height: 33,
                      }}
                      placeholder="Select Provider"
                      size="small"
                      type="text"
                      value={provider.provider}
                      onChange={(e) =>
                        handleChange(index, "provider", e.target.value)
                      }
                    >
                      {providerList.map((data) => (
                        <MenuItem value={data._id} sx={{ height: 30 }}>
                          {data?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </Grid>
                {isPipe17(provider) ? (
                  <Grid item xs={12} sm={6}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label
                        style={{
                          fontFamily: "Poppins",
                          fontSize: 13,
                          color: "var(--text-primary)",
                        }}
                      >
                        Pipe17 API key <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        disabled={viewOnly}
                        placeholder="Enter Pipe17 API key"
                        style={{
                          backgroundColor: "var(--bg-paper)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: 5,
                          padding: 5,
                          marginTop: 5,
                          fontSize: 13,
                        }}
                        type="text"
                        value={provider.api_key}
                        size="small"
                        onChange={(e) =>
                          handleChange(index, "api_key", e.target.value)
                        }
                      />
                    </div>
                  </Grid>
                ) : null}
                {isPipe17(provider) && provider.orgKeyNeeded ? (
                  <Grid item xs={12} sm={6}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label
                        style={{
                          fontFamily: "Poppins",
                          fontSize: 13,
                          color: "var(--text-primary)",
                        }}
                      >
                        Org key (orgKey) <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        disabled={viewOnly}
                        placeholder="Enter Pipe17 org key"
                        style={{
                          backgroundColor: "var(--bg-paper)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: 5,
                          padding: 5,
                          marginTop: 5,
                          fontSize: 13,
                        }}
                        type="text"
                        value={provider.org_key}
                        size="small"
                        onChange={(e) =>
                          handleChange(index, "org_key", e.target.value)
                        }
                      />
                      <span
                        style={{
                          fontFamily: "Poppins",
                          fontSize: 11,
                          color: "var(--text-secondary, #888)",
                          marginTop: 4,
                        }}
                      >
                        Pipe17 couldn&apos;t auto-detect the org key for this
                        account (no orders, products, or webhooks found yet)
                        — enter it manually.
                      </span>
                    </div>
                  </Grid>
                ) : null}
                {isAcenda(provider) ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <label style={{
                          fontFamily: "Poppins",
                          fontSize: 13,
                          color: "var(--text-primary)",
                        }}>
                          Organization <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          disabled={viewOnly}
                          placeholder="acme-org"
                          style={{
                          backgroundColor: "var(--bg-paper)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: 5,
                          padding: 5,
                          marginTop: 5,
                          fontSize: 13,
                        }}
                          type="text"
                          value={provider.organization || ""}
                          size="small"
                          onChange={(e) =>
                            handleChange(index, "organization", e.target.value)
                          }
                        />
                        <span style={{
                          fontFamily: "Poppins",
                          fontSize: 11,
                          color: "var(--text-secondary, #888)",
                          marginTop: 4,
                        }}>
                          Sent as the X-Astur-Organization header. Acenda
                          exposes no record to auto-detect it from.
                        </span>
                      </div>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <label style={{
                          fontFamily: "Poppins",
                          fontSize: 13,
                          color: "var(--text-primary)",
                        }}>
                          Credential type <span style={{ color: "red" }}>*</span>
                        </label>
                        <Select
                          disabled={viewOnly}
                          style={{
                            backgroundColor: "var(--bg-paper)",
                            color: "var(--text-primary)",
                            marginTop: 5,
                            fontSize: 13,
                            height: 33,
                          }}
                          size="small"
                          value={provider.auth_mode || AUTH_MODES.OAUTH}
                          onChange={(e) =>
                            handleChange(index, "auth_mode", e.target.value)
                          }
                        >
                          <MenuItem value={AUTH_MODES.OAUTH} sx={{ height: 30 }}>
                            OAuth2 client credentials
                          </MenuItem>
                          <MenuItem value={AUTH_MODES.API_KEY} sx={{ height: 30 }}>
                            Static API key
                          </MenuItem>
                        </Select>
                      </div>
                    </Grid>

                    {(provider.auth_mode || AUTH_MODES.OAUTH) ===
                    AUTH_MODES.OAUTH ? (
                      <>
                        <Grid item xs={12} sm={6}>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{
                          fontFamily: "Poppins",
                          fontSize: 13,
                          color: "var(--text-primary)",
                        }}>
                              Acenda client ID{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              disabled={viewOnly}
                              placeholder="api_client_org_<org>_XXXXXXXX"
                              style={{
                          backgroundColor: "var(--bg-paper)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: 5,
                          padding: 5,
                          marginTop: 5,
                          fontSize: 13,
                        }}
                              type="text"
                              value={provider.acenda_client_id || ""}
                              size="small"
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  "acenda_client_id",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{
                          fontFamily: "Poppins",
                          fontSize: 13,
                          color: "var(--text-primary)",
                        }}>
                              Acenda client secret{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              disabled={viewOnly}
                              placeholder="Enter Acenda client secret"
                              style={{
                          backgroundColor: "var(--bg-paper)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: 5,
                          padding: 5,
                          marginTop: 5,
                          fontSize: 13,
                        }}
                              type="password"
                              value={provider.acenda_client_secret || ""}
                              size="small"
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  "acenda_client_secret",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12} sm={6}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <label style={{
                          fontFamily: "Poppins",
                          fontSize: 13,
                          color: "var(--text-primary)",
                        }}>
                            Acenda API key <span style={{ color: "red" }}>*</span>
                          </label>
                          <input
                            disabled={viewOnly}
                            placeholder="Enter Acenda API key"
                            style={{
                          backgroundColor: "var(--bg-paper)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: 5,
                          padding: 5,
                          marginTop: 5,
                          fontSize: 13,
                        }}
                            type="password"
                            value={provider.api_key || ""}
                            size="small"
                            onChange={(e) =>
                              handleChange(index, "api_key", e.target.value)
                            }
                          />
                          <span style={{
                          fontFamily: "Poppins",
                          fontSize: 11,
                          color: "var(--text-secondary, #888)",
                          marginTop: 4,
                        }}>
                            Acenda admin panel, under Users &rarr; API Keys.
                          </span>
                        </div>
                      </Grid>
                    )}
                  </>
                ) : null}
                {!isDirectOnboard(provider) ? (
                  <Grid item xs={12} sm={6}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label
                        style={{
                          fontFamily: "Poppins",
                          fontSize: 13,
                          color: "var(--text-primary)",
                        }}
                      >
                        Shop Name <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        disabled={viewOnly}
                        placeholder="Enter Shop Name"
                        style={{
                          backgroundColor: "var(--bg-paper)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: 5,
                          padding: 5,
                          marginTop: 5,
                          fontSize: 13,
                        }}
                        type="text"
                        value={provider.shop_name}
                        size="small"
                        onChange={(e) =>
                          handleChange(index, "shop_name", e.target.value)
                        }
                      />
                    </div>
                  </Grid>
                ) : null}
              </Grid>
              {isPipe17(provider) ? null : (
                <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} mt={2}>
                <label
                  style={{
                    fontFamily: "Poppins",
                    fontSize: 13,
                    color: "var(--text-primary)",
                  }}
                >
                  Extra Information
                </label>

                {provider.extra_info?.map((x, i) => (
                  <Grid container spacing={2} key={i} style={{ marginTop: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <label style={{ fontFamily: "Poppins", fontSize: 12, color: "var(--text-primary)" }}>
                        Key
                      </label>
                      <input
                        disabled={viewOnly}
                        placeholder="Key"
                        style={{
                          backgroundColor: "var(--bg-paper)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: 5,
                          padding: 5,
                          marginTop: 5,
                          fontSize: 13,
                          width: "100%",
                        }}
                        type="text"
                        value={x.key}
                        onChange={(e) =>
                          manageExtraInfo(index, i, "key", e.target.value)
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <label style={{ fontFamily: "Poppins", fontSize: 12, color: "var(--text-primary)" }}>
                        Value
                      </label>
                      <div style={{ display: "flex", width: "100%" }}>
                        <input
                          disabled={viewOnly}
                          placeholder="Value"
                          style={{
                            backgroundColor: "var(--bg-paper)",
                            color: "var(--text-primary)",
                            border: "1px solid var(--border-color)",
                            borderRadius: 5,
                            padding: 5,
                            marginTop: 5,
                            fontSize: 13,
                            width: "100%",
                          }}
                          type="text"
                          value={x.value}
                          onChange={(e) =>
                            manageExtraInfo(index, i, "value", e.target.value)
                          }
                        />
                        {viewOnly ? (
                          <></>
                        ) : (
                          <IconButton
                            style={{
                              backgroundColor: "var(--bg-paper)",
                              color: "var(--text-primary)",
                              backgroundColor:
                                i === provider.extra_info.length - 1
                                  ? "var(--primary-blue)"
                                  : "transparent",
                              width: 30,
                              height: 30,
                              borderRadius: "4px",
                              marginLeft: 10,
                              color: "white",
                              marginTop: 3,
                            }}
                            onClick={(e) =>
                              i === provider.extra_info.length - 1
                                ? addNewAuthDetails(e, index)
                                : removeAuthDetail(index, i)
                            }
                          >
                            {i === provider.extra_info.length - 1 ? (
                              <AddIcon style={{ color: "white" }} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        )}
                      </div>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              </>
              )}
            </Grid>
          }
        </Paper>
      ))}
      <div
        style={{
          // backgroundColor: "var(--bg-paper)",
          color: "var(--text-primary)",
          marginTop: "16px",
          width: "100%",
          justifyContent: "flex-end",
          display: "flex",
        }}
      >
        {selectedData?.length ? (
          <></>
        ) : (
          <Button onClick={handleAddProvider} style={{ textTransform: "none" }}>
            <CustomTypography
              fontSize={"14px"}
              color={"var(--primary-blue)"}
              fontWeight={400}
            >{`+ Add Provider`}</CustomTypography>
          </Button>
        )}
      </div>

      <DialogActions style={{ padding: "16px" }}>
        <Button
          style={{
            backgroundColor: "var(--bg-paper)",
            color: "var(--text-primary)",
            fontSize: "12px",
            fontFamily: "Poppins",
            textTransform: "none",
          }}
          onClick={handleClose}
          variant="outlined"
        >
          Cancel
        </Button>
        {viewOnly ? (
          <></>
        ) : (
          <Button
            style={{
              backgroundColor: "var(--bg-paper)",
              color: "var(--text-primary)",
              fontSize: "12px",
              fontFamily: "Poppins",
              textTransform: "none",
            }}
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isFormValid}
          >
            Add
          </Button>
        )}
      </DialogActions>
    </form>
  );
};

const OMSProviderModal = ({
  setOpen,
  onClose,
  selectedClient,
  selectedData,
  onAddProviderData,
  setLoading,
  loading,
  onDeleteSuccess,
  providerList = [],
  viewOnly,
}) => {
  const handleClose = () => {
    setOpen(false);
    onClose && onClose();
  };

  return (
    <OMSManagementForm
      viewOnly={viewOnly}
      onDeleteSuccess={onDeleteSuccess}
      setLoading={setLoading}
      loading={loading}
      onAddProviderData={onAddProviderData}
      selectedData={selectedData}
      handleClose={handleClose}
      selectedClient={selectedClient}
      providerList={providerList}
    />
  );
};

export default OMSProviderModal;
